import { useAtom } from "jotai";
import type { PropsWithChildren } from "react";
import { useEffect } from "react";
import { useIsLogged } from "../../../hooks/auth/use-is-logged/use-is-logged";
import { assignUpgradeFirstExperiment } from "../../../lib/mixpanel/experiments/upgrade-first-experiment/assign-upgrade-first-experiment";
import featureFlag from "../../../lib/feature-flag/feature-flag";
import mixpanel from "../../../lib/mixpanel/mixpanel.config";
import { blockMixpanelForQaAccount } from "../../../lib/mixpanel/qa-account/block-mixpanel-for-qa-account";
import { markQaAccountSession } from "../../../lib/qa-accounts/qa-account-email";
import type { IAccountWithEligibility } from "../../../types/account-with-eligibility.interface";
import { useCurrentUserSubscriptionTier } from "../../hooks/use-current-user-subscription-tier/use-current-user-subscription-tier";
import { useAbility } from "../../hooks/useAbilityRules";
import { useAccount } from "../../hooks/useAccount";
import { AllActionsEnum } from "../../reducers/actions";
import { identifyUser } from "../../services/identifyUser";
import { RootStore } from "../../stores/app";
import { retrieveQuestionnaire } from "../retrieve-questionnaire/retrieve-questionnaire";

export function InitializeApp({ children }: Readonly<PropsWithChildren<{}>>): JSX.Element {
  const [rootStore, setRootStore] = useAtom(RootStore);
  const abilityRules = useAbility();
  const isLogged = useIsLogged();
  const { accountData } = useAccount();
  const subscriptionTier = useCurrentUserSubscriptionTier();

  useEffect(
    function setInitialUserData() {
      let isCancelled = false;

      if (accountData) {
        markQaAccountSession(accountData.email);
        identifyUser(accountData);
        blockMixpanelForQaAccount();
        mixpanel.identify(`${accountData?.id}`);

        featureFlag
          .updateContextWithAccountData({
            upgradeFirstEligible: (accountData as IAccountWithEligibility).upgradeFirstEligible ?? false,
          })
          .then(function onContextReady() {
            if (isCancelled) {
              return;
            }
            assignUpgradeFirstExperiment(accountData as IAccountWithEligibility);
          })
          .catch(function handleAssignmentError(error) {
            console.error("Failed to assign upgrade-first experiment:", error);
          });

        mixpanel.people.set({
          email: accountData.email,
          name: accountData.name,
          citizenId: accountData.citizen?.id,
          accountId: accountData.id,
          createdAt: accountData.created_at ? new Date(accountData.created_at) : undefined,
          tierName: subscriptionTier.currentUserTier,
          tierFrequency: subscriptionTier.currentUserTierFrequency,
          tierStartDate: subscriptionTier.currentUserTierStartDate,
          tierEndDate: subscriptionTier.currentUserTierEndDate,
        });
      }

      const currentName = rootStore.updates?.formValues?.Nome;
      if (accountData?.name && accountData.name !== currentName) {
        setRootStore({ type: AllActionsEnum.SET_CITIZEN_NAME, payload: { name: `${accountData.name?.trim()}` } });
      }

      return function cleanup() {
        isCancelled = true;
      };
    },
    [accountData, rootStore.updates?.formValues?.Nome]
  );

  useEffect(
    function lastStepForInitializeApp() {
      if (!isLogged || abilityRules.cannot("readOne", "Citizen")) {
        return;
      }

      retrieveQuestionnaire(setRootStore);
    },
    [abilityRules.rules]
  );

  return <>{children}</>;
}
